/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: notifications entity configuration
 * Generator: scripts/generate-api-clients/generate-api-clients.ts
 *
 * This file contains the API client for the notifications table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the notifications API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, organization_id, user_id, notification_type_code, title, message, importance, project_id, job_id, lot_id, area_id, activity_id, change_order_id, extra_work_order_id, inventory_item_id, purchase_order_id, document_id, action_url, action_text, is_read, read_at, is_delivered_in_app, is_delivered_email, is_delivered_sms, created_at, created_by]
 * Searchable fields: []
 * Filterable fields: [id, organization_id, user_id, project_id, job_id, lot_id, area_id, activity_id, change_order_id, extra_work_order_id, inventory_item_id, purchase_order_id, document_id, is_read, is_delivered_in_app, is_delivered_email, is_delivered_sms]
 */

import { httpClient } from '../http-client.js';
import { globalSearchConfig } from '../../../config/search/global-search-config.js';
import { matchesAllTerms } from '../../../config/search/search-utils.js';
import type {
  NotificationsRow,
  NotificationsInsert,
  NotificationsUpdate
} from '../generated_schemas/generated-notifications-schema.js';
import { NotificationsQueryOptions } from '../generated_schemas/generated-notifications-schema.js';
export type { NotificationsQueryOptions };

// Internal response type with non-null data (not exported to avoid conflicts with schema's Response type)
interface NotificationsClientResponse {
  data: NotificationsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for notifications table operations
 *
 * @description Provides type-safe access to notifications API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class NotificationsClient {
  private readonly basePath = '/notifications';

  /**
   * Get all notifications records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of notifications records
   */
  async getAll(options?: NotificationsQueryOptions): Promise<NotificationsRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<NotificationsClientResponse>(url);
    return response.data;
  }

  /**
   * Get a single notifications record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the notifications record
   */
  async getById(id: string): Promise<NotificationsRow> {
    const response = await httpClient.get<{
      data: NotificationsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new notifications record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created notifications record
   */
  async create(data: NotificationsInsert): Promise<NotificationsRow> {
    const response = await httpClient.post<{
      data: NotificationsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing notifications record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated notifications record
   */
  async update(id: string, data: NotificationsUpdate): Promise<NotificationsRow> {
    const response = await httpClient.put<{
      data: NotificationsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a notifications record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple notifications records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created notifications records
   */
  async createMany(data: NotificationsInsert[]): Promise<NotificationsRow[]> {
    const response = await httpClient.post<{
      data: NotificationsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple notifications records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search notifications with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    query: string,
    columns?: string[],
    options?: NotificationsQueryOptions
  ): Promise<NotificationsClientResponse> {
    throw new Error('Search operations are not available for notifications - no searchable fields configured');
  }

  /**
   * Get count of notifications records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a notifications record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'notifications',
      publicFields: ['id', 'organization_id', 'user_id', 'notification_type_code', 'title', 'message', 'importance', 'project_id', 'job_id', 'lot_id', 'area_id', 'activity_id', 'change_order_id', 'extra_work_order_id', 'inventory_item_id', 'purchase_order_id', 'document_id', 'action_url', 'action_text', 'is_read', 'read_at', 'is_delivered_in_app', 'is_delivered_email', 'is_delivered_sms', 'created_at', 'created_by'],
      searchableFields: [],
      filterableFields: ['id', 'organization_id', 'user_id', 'project_id', 'job_id', 'lot_id', 'area_id', 'activity_id', 'change_order_id', 'extra_work_order_id', 'inventory_item_id', 'purchase_order_id', 'document_id', 'is_read', 'is_delivered_in_app', 'is_delivered_email', 'is_delivered_sms'],
      sortableFields: ['created_at'],
      readonlyFields: ['id', 'created_at'],
      requiredFields: ['organization_id', 'user_id', 'notification_type_code', 'title', 'message'],
    };
  }
}

// Export singleton instance for convenience
export const notificationsClient = new NotificationsClient();
